import Button from '@/shared/Button';
import Div from '@/shared/Div';
import Span from '@/shared/Span';
import React, { Fragment, memo, useEffect } from 'react';
import Circle from '../Icons/Circle';
import Input from '@/shared/Input';
import { Controller, useForm } from 'react-hook-form';
import { inputOnlyNumbers } from '@/utils/formValidation';
import LocalStorage from '@/utils/localStorage';
import {
  ECOM_INVENTORY_STATUS,
  ECOM_INVENTORY_STATUS_MESSAGES,
  EFL_PINCODE,
} from '@/constants/app';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { inventoryStatus } from '@/actions/ecom/checkInventoryStatus';
import Form from '@/shared/Form';
import date from '@/utils/date';
import dlv from 'dlv';
import { useEcomProductContext } from '@/context/ecomProductContext';
import { MapType } from '@/types';
import { Flags } from '@/types/pdp';

type FormInputs = {
  pinCode: string;
};
const Delivery = ({
  dataTestId = 'product-delivery',
  title,
  productId,
  materialId,
  installation,
  shipping,
  flags,
}: {
  dataTestId?: string;
  title?: string;
  productId: string;
  materialId: string;
  installation: any;
  shipping: any;
  flags: Flags;
}) => {
  const eflPincode = LocalStorage.get(EFL_PINCODE);
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: {
      pinCode: eflPincode ?? '',
    },
  });
  const { saleable } = flags;
  const { state: ecomProductState, dispatch: ecomProductDispatch } = useEcomProductContext();

  const product = dlv(ecomProductState, `productData.${materialId}`, null);
  const fetch = (...args: any) => inventoryStatus.apply(null, args);
  const { isLoading, promisifyTrigger, isError } = useTriggerRequest({ fetch });

  useEffect(() => {
    const fetchInventory = async (productId: string) => {
      const productData: any = {
        productIds: [productId],
      };
      if (eflPincode) {
        setValue('pinCode', eflPincode);
        productData.pinCode = eflPincode;
      }
      const payload: MapType = {
        materialId,
      };
      try {
        const response = await promisifyTrigger(productData);
        const inventory = dlv(response, 'data.data.inventories.0', null);
        payload.inventory = inventory;
      } catch (error) {
        payload.inventory = null;
      }
      ecomProductDispatch.updateInventory(payload);
    };

    if (!product?.inventory && saleable) {
      fetchInventory(productId);
    }
  }, [eflPincode, materialId, product?.inventory, productId, saleable]);

  //pincode - 202124 materialId - GSDDILYNXWLSHW
  const onSubmit = async (data: FormInputs) => {
    const { pinCode } = data;
    const response = await promisifyTrigger({ productIds: [productId], pinCode });
    const inventory = dlv(response, 'data.data.inventories.0', null);
    ecomProductDispatch.updateInventory({
      materialId,
      inventory,
    });
    LocalStorage.set(EFL_PINCODE, pinCode);
  };

  const {
    availability,
    deliveryEstimate,
    inventoryStatus: status,
  } = dlv(product, 'inventory', {}) || {};

  const getInventoryStatusMessage = (inventoryStatus: string) => {
    if (inventoryStatus === ECOM_INVENTORY_STATUS.LOW_STOCK) {
      if (installation.finalAmt === 0 && shipping.finalAmt === 0) {
        return 'Free shipping & installation';
      } else if (installation.finalAmt === 0) {
        return 'Free installation';
      } else if (shipping.finalAmt === 0) {
        return 'Free shipping';
      }
      return ECOM_INVENTORY_STATUS_MESSAGES[inventoryStatus];
    }
    if (inventoryStatus === ECOM_INVENTORY_STATUS.IN_STOCK) {
      if (installation.finalAmt === 0 && shipping.finalAmt === 0) {
        return ECOM_INVENTORY_STATUS_MESSAGES[inventoryStatus] + ' - Free shipping & installation';
      } else if (installation.finalAmt === 0) {
        return ECOM_INVENTORY_STATUS_MESSAGES[inventoryStatus] + ' - Free installation';
      } else if (shipping.finalAmt === 0) {
        return ECOM_INVENTORY_STATUS_MESSAGES[inventoryStatus] + ' - Free shipping';
      } else {
        return ECOM_INVENTORY_STATUS_MESSAGES[inventoryStatus];
      }
    } else if (inventoryStatus === ECOM_INVENTORY_STATUS.OUT_OF_STOCK && eflPincode) {
      return 'Not available at this pin code';
    } else if (inventoryStatus === ECOM_INVENTORY_STATUS.OUT_OF_STOCK) {
      return ECOM_INVENTORY_STATUS_MESSAGES[inventoryStatus];
    }
  };

  const getDeliveryEstimate = (
    deliveryEstimate: string | null,
    availability: boolean,
    inventoryStatus: string,
  ) => {
    // If availablity is not defined, we return null
    if (availability === undefined) return null;
    // Case when we are checking without pincode
    if (!deliveryEstimate) {
      return <Span className="text-body-base">{getInventoryStatusMessage(inventoryStatus)}</Span>;
    }

    //if availabity is True and deliveryEstimate is not null, we are checking with pincode
    else if (availability) {
      return (
        <Fragment>
          {`Delivery by ${date.format(deliveryEstimate, {
            month: 'long',
            day: '2-digit',
          })} - `}
          <Span className="text-body-base">{getInventoryStatusMessage(inventoryStatus)}</Span>
        </Fragment>
      );
    }
    // When availability is false at that particular pincode
    else return 'Not available at this pin code';
  };

  const renderStatus = () => {
    if (isLoading || isError) return null;
    return (
      <Div className="text-cti-primary text-body-base lg:text-body-lg">
        <Span className="text-callout-small lg:text-callout-normal">
          {getDeliveryEstimate(deliveryEstimate, availability, status)}
        </Span>
      </Div>
    );
  };

  const renderCircle = (availability: boolean, deliveryEstimate: string) => {
    if (
      availability === undefined ||
      (!eflPincode && status === ECOM_INVENTORY_STATUS.IN_STOCK) ||
      status === ECOM_INVENTORY_STATUS.LOW_STOCK
    )
      return null;
    else if (availability) return <Circle />;
    else return <Circle fillColor="#D45C19" />;
  };

  return saleable ? (
    <Div data-test-id={dataTestId} className="mt-s9 md:mt-s10">
      <Div className="text-h-3 mb-s4 lg:text-hd-3">{title}</Div>
      <Div className="felx flex-col rounded-cm border border-solid border-co-subtle bg-cs-secondary p-s5 px-s5 py-5">
        <Form
          className="flex justify-between items-start gap-x-s4 py-s3"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Div className="text-body-lg flex-grow">
            <Controller
              name="pinCode"
              control={control}
              rules={{
                maxLength: 6,
                minLength: 6,
                required: true,
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  size="xs"
                  className="pb-s3 border-0 border-b border-co-bold text-body-lg text-cti-primary rounded-none !bg-transparent !outline-none !focus:outline-none w-full"
                  {...field}
                  placeholder="Enter Pincode"
                  onKeyPress={inputOnlyNumbers}
                  maxLength={6}
                />
              )}
            />
          </Div>
          <Button
            theme="secondary"
            size="sm"
            className="py-s3 px-s4 rounded-cxs w-s11"
            type="submit"
            disabled={!isValid || isLoading}
          >
            {eflPincode ? 'Change' : 'Check'}
          </Button>
        </Form>
        <Div className="flex gap-s3 items-start mt-s4 lg:mt-s3">
          <Div className="mt-1.5">{renderCircle(availability, deliveryEstimate)}</Div>
          {renderStatus()}
        </Div>
      </Div>
    </Div>
  ) : (
    <></>
  );
};

export default memo(Delivery);
